import type { MpUserSelfWecomPush } from './mpUserSelfWecomPush';
import {
  defaultMpUserSelfWecomPush,
  normalizeMpUserSelfWecomPush,
  mpUserWecomDefaultReviewTemplate,
  mpUserWecomDefaultPublishedTemplate,
  mpUserWecomDefaultProfileTemplate,
} from './mpUserSelfWecomPush';

export type { MpUserSelfWecomPush } from './mpUserSelfWecomPush';
export {
  defaultMpUserSelfWecomPush,
  normalizeMpUserSelfWecomPush,
  mpUserWecomDefaultReviewTemplate,
  mpUserWecomDefaultPublishedTemplate,
  mpUserWecomDefaultProfileTemplate,
};

export type MpLegalDocs = {
  version: string;
  user_agreement: string;
  privacy_policy: string;
  publish_rules: string;
};

export type MiniproPublishMode = 'legacy' | 'user_self';

export const PUBLISH_MODE_LABELS: Record<MiniproPublishMode, string> = {
  legacy: '传统运营（站长/表单/客服导流）',
  user_self: '用户自助发布（二手闲置 · 微信登录）',
};

export function defaultMpLegalDocs(platformName?: string): MpLegalDocs {
  const name = (platformName || '').trim() || '本平台';
  const version = '2026-06-11';
  return {
    version,
    user_agreement: `# ${name}用户服务协议\n\n更新日期：2026年6月11日\n\n（可在管理端编辑完整正文；保存后小程序端同步生效。）`,
    privacy_policy: `# ${name}隐私政策\n\n更新日期：2026年6月11日\n\n（可在管理端编辑完整正文；保存后小程序端同步生效。）`,
    publish_rules: `# ${name}二手闲置发布规范\n\n更新日期：2026年6月11日\n\n（可在管理端编辑完整正文；保存后小程序端同步生效。）`,
  };
}

export function normalizeMpLegalDocs(raw: unknown, platformName?: string): MpLegalDocs {
  const d = defaultMpLegalDocs(platformName);
  if (!raw || typeof raw !== 'object') return d;
  const o = raw as Record<string, unknown>;
  return {
    version: String(o.version || d.version).trim() || d.version,
    user_agreement: String(o.user_agreement || d.user_agreement).trim() || d.user_agreement,
    privacy_policy: String(o.privacy_policy || d.privacy_policy).trim() || d.privacy_policy,
    publish_rules: String(o.publish_rules || d.publish_rules).trim() || d.publish_rules,
  };
}

export function normalizePublishMode(raw: unknown): MiniproPublishMode {
  return raw === 'user_self' ? 'user_self' : 'legacy';
}


export type MpUserSelfSettings = {
  review_entry: boolean;
  review_entry_edit: boolean;
  review_nickname: boolean;
  review_avatar: boolean;
  /** 疑似重复发布时自动进入待审（不拦截发布） */
  dedupe_entry_enabled: boolean;
  /** 与多少天内的历史发布比对 */
  dedupe_lookback_days: number;
  /** 酒类/烟草/药品等资质准入·高危定邀类目自动进审（个人用户不可发） */
  restricted_category_review_enabled: boolean;
  /** 疑似非二手信息全部进审核 */
  non_secondhand_review_all: boolean;
  /** 疑似招聘信息自动进审核 */
  non_secondhand_review_job: boolean;
  /** 疑似房产信息自动进审核 */
  non_secondhand_review_housing: boolean;
  /** 疑似相亲信息自动进审核 */
  non_secondhand_review_dating: boolean;
  wecom_push: MpUserSelfWecomPush;
};

export function defaultMpUserSelfSettings(): MpUserSelfSettings {
  return {
    review_entry: true,
    review_entry_edit: true,
    review_nickname: true,
    review_avatar: true,
    dedupe_entry_enabled: true,
    dedupe_lookback_days: 120,
    restricted_category_review_enabled: true,
    non_secondhand_review_all: false,
    non_secondhand_review_job: false,
    non_secondhand_review_housing: false,
    non_secondhand_review_dating: false,
    wecom_push: defaultMpUserSelfWecomPush(),
  };
}

export function normalizeMpUserSelfSettings(raw: unknown): MpUserSelfSettings {
  const d = defaultMpUserSelfSettings();
  if (!raw || typeof raw !== 'object') return d;
  const o = raw as Record<string, unknown>;
  const reviewAll = o.non_secondhand_review_all !== undefined ? !!o.non_secondhand_review_all : d.non_secondhand_review_all;
  return {
    review_entry: o.review_entry !== undefined ? !!o.review_entry : d.review_entry,
    review_entry_edit: o.review_entry_edit !== undefined ? !!o.review_entry_edit : d.review_entry_edit,
    review_nickname: o.review_nickname !== undefined ? !!o.review_nickname : d.review_nickname,
    review_avatar: o.review_avatar !== undefined ? !!o.review_avatar : d.review_avatar,
    dedupe_entry_enabled: o.dedupe_entry_enabled !== undefined ? !!o.dedupe_entry_enabled : d.dedupe_entry_enabled,
    dedupe_lookback_days: Math.min(365, Math.max(30, Number(o.dedupe_lookback_days) || d.dedupe_lookback_days)),
    restricted_category_review_enabled:
      o.restricted_category_review_enabled !== undefined
        ? !!o.restricted_category_review_enabled
        : d.restricted_category_review_enabled,
    non_secondhand_review_all: reviewAll,
    non_secondhand_review_job: reviewAll
      ? false
      : o.non_secondhand_review_job !== undefined
        ? !!o.non_secondhand_review_job
        : d.non_secondhand_review_job,
    non_secondhand_review_housing: reviewAll
      ? false
      : o.non_secondhand_review_housing !== undefined
        ? !!o.non_secondhand_review_housing
        : d.non_secondhand_review_housing,
    non_secondhand_review_dating: reviewAll
      ? false
      : o.non_secondhand_review_dating !== undefined
        ? !!o.non_secondhand_review_dating
        : d.non_secondhand_review_dating,
    wecom_push: normalizeMpUserSelfWecomPush(o.wecom_push),
  };
}
